'use client';

import { MEDIA_CACHE_VERSION } from '@/config/media-config';
import { normalizeMediaUrl } from '@/lib/url-normalization';

/**
 * Global Video Poster Cache
 * 
 * PERFORMANCE: Prevents duplicate video downloads for poster generation.
 * When multiple S3FilePreview components render the same video,
 * only ONE hidden video element is created to extract the poster frame.
 * 
 * This reduces network payload significantly (e.g., a 2MB video downloaded 10 times = 20MB).
 */

interface PosterCacheEntry {
  posterUrl: string | null;
  extractionPromise: Promise<string | null> | null;
  timestamp: number;
}

class VideoPosterCache {
  private cache = new Map<string, PosterCacheEntry>();
  private extractionInProgress = new Set<string>();
  
  // Cache TTL: 30 minutes
  private readonly CACHE_TTL = 30 * 60 * 1000;

  /**
   * Normalize video URL to create consistent cache key
   * CRITICAL: Must include version parameter to match the actual URL used for loading
   * Uses centralized URL normalization utility
   */
  private normalizeUrl(url: string): string {
    if (!url) return url;
    
    // Use centralized normalization with version parameter for cache key
    // Import at top level to avoid require() in method
    const normalized = normalizeMediaUrl(url) || url;
    
    return normalized;
  }

  /**
   * Check if poster is already cached or being extracted
   */
  hasPoster(url: string): boolean {
    const key = this.normalizeUrl(url);
    const entry = this.cache.get(key);
    
    if (entry && entry.posterUrl && (Date.now() - entry.timestamp) < this.CACHE_TTL) {
      return true;
    }
    
    return false;
  }

  /**
   * Check if extraction is already in progress for this URL
   */
  isExtracting(url: string): boolean {
    const key = this.normalizeUrl(url);
    return this.extractionInProgress.has(key);
  }

  /**
   * Get cached poster URL
   */
  getPoster(url: string): string | null {
    const key = this.normalizeUrl(url);
    const entry = this.cache.get(key);
    
    if (entry && entry.posterUrl && (Date.now() - entry.timestamp) < this.CACHE_TTL) {
      return entry.posterUrl;
    }
    
    return null;
  }

  /**
   * Get or start poster extraction
   * Returns promise that resolves to poster URL (or null if extraction fails)
   */
  async getOrExtractPoster(
    url: string,
    extractFn: () => Promise<string | null>
  ): Promise<string | null> {
    const key = this.normalizeUrl(url);
    
    // Check if already cached
    const existingEntry = this.cache.get(key);
    if (existingEntry && existingEntry.posterUrl && (Date.now() - existingEntry.timestamp) < this.CACHE_TTL) {
      return existingEntry.posterUrl;
    }
    
    // Check if extraction is already in progress
    if (existingEntry?.extractionPromise) {
      return existingEntry.extractionPromise;
    }
    
    // Start extraction
    this.extractionInProgress.add(key);
    
    const extractionPromise = extractFn().then((posterUrl) => {
      this.cache.set(key, {
        posterUrl,
        extractionPromise: null,
        timestamp: Date.now(),
      });
      this.extractionInProgress.delete(key);
      return posterUrl;
    }).catch(() => {
      this.extractionInProgress.delete(key);
      return null;
    });
    
    this.cache.set(key, {
      posterUrl: null,
      extractionPromise,
      timestamp: Date.now(),
    });
    
    return extractionPromise;
  }

  /**
   * Manually set a poster URL (e.g., from server-generated thumbnail)
   */
  setPoster(url: string, posterUrl: string): void {
    const key = this.normalizeUrl(url);
    this.cache.set(key, {
      posterUrl,
      extractionPromise: null,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if ((now - entry.timestamp) >= this.CACHE_TTL) {
        // Revoke blob URLs before removing
        if (entry.posterUrl && entry.posterUrl.startsWith('blob:')) {
          URL.revokeObjectURL(entry.posterUrl);
        }
        this.cache.delete(key);
      }
    }
  }
}

// Global singleton
export const videoPosterCache = new VideoPosterCache();

// Cleanup expired entries periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    videoPosterCache.cleanup();
  }, 5 * 60 * 1000); // Every 5 minutes
}

